import { useState, useEffect, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNostr } from '@nostrify/react';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { Author } from '@/components/Author';
import { kebabToTitleCase } from '@/lib/utils';
import { ListStats } from '@/components/ListStats';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { ScryfallCard, ScryfallCardWithCount } from '@/types/scryfall';
import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAppContext } from '@/hooks/useAppContext';
import { EditListSheet } from '@/components/EditListSheet';
import { ShoppingBag, Search, ArrowLeft, EyeOff } from 'lucide-react';
import { ShareListDialog } from '@/components/ShareListDialog';
import { CardDetailsDialog } from '@/components/CardDetailsDialog';
import { ListActions } from '@/components/ListActions';
import { CardMenu } from '@/components/CardMenu';
import { ChangeArtDialog } from '@/components/ChangeArtDialog';
import { Input } from '@/components/ui/input';
import { SortControls } from '@/components/SortControls';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { MTGCard } from '@/components/MTGCard';
import { PrintDecklistButton } from '@/components/PrintDecklistButton';
import { Deck, isDeckEvent, isFormatTag, transformDeckEvent } from '@/lib/nostr/deck';
import { fetchCardsFromIdentifiers } from '@/lib/card-importer';
import { useEDHRec } from '@/hooks/useEDHRec';
import { EDHRecCard } from '@/components/EDHRecCard';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Terminal } from 'lucide-react';
import { QuickAddBar } from '@/components/QuickAddBar';
import { ListLoadingIndicator } from '@/components/ListLoadingIndicator';

type GroupByOption = 'none' | 'type' | 'color';
type View = 'deck' | 'edhrec';

const getPrimaryCardType = (card: ScryfallCard) => {
  const types = card.type_line.split(' // ')[0].split(' — ')[0].split(' ');
  const priority = ['Creature', 'Artifact', 'Enchantment', 'Planeswalker', 'Battle', 'Instant', 'Sorcery', 'Land', 'Tribal'];
  for (const type of priority) {
    if (types.includes(type)) {
      return type;
    }
  }
  return types[0] || 'Unknown';
};

const ListPage = () => {
  const queryClient = useQueryClient();
  const { naddr } = useParams<{ naddr: string }>();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { nostr } = useNostr();
  const { user } = useCurrentUser();
  const { config } = useAppContext();
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [detailsCard, setDetailsCard] = useState<ScryfallCard | null>(null);
  const [artChangeCard, setArtChangeCard] = useState<ScryfallCardWithCount | null>(null);
  const [groupBy, setGroupBy] = useState<GroupByOption>('type');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');
  const [editableTitle, setEditableTitle] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [view, setView] = useState<View>('deck');
  const [activeFilter, setActiveFilter] = useState<{ type: string, value: string | number } | null>(null);

  const cardIsLegal = (card: ScryfallCard, format: string) => {
    if (!format) return true;
    if (format === 'pauper-commander') {
      const isCommander = deck?.commanders.includes(card.name);
      const isLegalInPauper = card.legalities?.pauper === 'legal';
      const isLegalInCommander = card.legalities?.commander === 'legal';
      const isUncommon = card.rarity === 'uncommon';

      if (isCommander && isUncommon && isLegalInCommander) {
        return true;
      }

      return isLegalInPauper && isLegalInCommander;
    }
    return card.legalities?.[format] === 'legal';
  }

  const validCardTypes = new Set(['Creature', 'Instant', 'Sorcery', 'Land', 'Planeswalker', 'Artifact', 'Enchantment', 'Tribal', 'Battle']);

  const getCardTypes = (card: ScryfallCard) => {
    const allTypes = card.type_line.split(' // ')[0].split(' — ')[0].split(' ');
    return allTypes.filter(type => validCardTypes.has(type));
  };

  const { mutate: getRecommendations, data: edhrecData, isPending: isEdhrecPending, isError: isEdhrecError } = useEDHRec();

  const { data: deck, refetch: refetchList } = useQuery<Deck | null>({
    queryKey: ['list', naddr],
    queryFn: async () => {
      if (state?.deck) return state.deck;
      if (!naddr) return null;

      const decoded = nip19.decode(naddr);
      if (decoded.type !== 'naddr') return null;

      const filter = {
        kinds: [decoded.data.kind],
        authors: [decoded.data.pubkey],
        '#d': [decoded.data.identifier],
        limit: 1,
      };
      const events = await nostr.query([filter]);

      const event = events[0];
      if (!event || !isDeckEvent(event)) return null;

      return transformDeckEvent(event);
    },
    enabled: !!naddr || !!state?.deck,
  });

  const { data: scryfallCards, isLoading: isLoadingCards, refetch: refetchCards } = useQuery({
    queryKey: ['list-cards', deck?.cards.map(c => `${c.name}-${c.set_id}-${c.art_id}`)],
    queryFn: async () => {
      if (!deck) return [];

      const identifiers = deck.cards.map(c => ({
        count: c.quantity,
        name: c.name,
        set: c.set_id,
        collector_number: c.art_id,
        is_foil: c.foil || '',
      }));

      const result = await fetchCardsFromIdentifiers(identifiers, config.scryfallApiUrl);
      return result;
    },
    enabled: !!deck,
  });

  const { data: sideboardCards, isLoading: _isLoadingSideboard, refetch: refetchSideboard } = useQuery({
    queryKey: ['sideboard-cards', deck?.sideboard.map(c => `${c.name}-${c.set_id}-${c.art_id}`)],
    queryFn: async () => {
      if (!deck) return [];

      const identifiers = deck.sideboard.map(c => ({
        count: c.quantity,
        name: c.name,
        set: c.set_id,
        collector_number: c.art_id,
        is_foil: c.foil || '',
      }));

      const result = await fetchCardsFromIdentifiers(identifiers, config.scryfallApiUrl);
      return result;
    },
    enabled: !!deck,
  });

  useEffect(() => {
    if (deck?.title) {
      setEditableTitle(deck.title);
    }
  }, [deck?.title]);

  const listTitle = deck?.title || 'Card List';
    const listFormats = deck?.event.tags.filter(isFormatTag).map(t => t[1]) || [];
  const listFormat = listFormats[0] || '';
  const isUnlisted = deck?.event.tags.some(t => t[0] === 't' && t[1] === 'unlisted');
  const imageUrl = deck?.event.tags.find(t => t[0] === 'banner')?.[1];
  const authorPubkey = deck?.event.pubkey;
  const allScryfallCards = [...(scryfallCards || []), ...(sideboardCards || [])];
  const commanders = allScryfallCards?.filter(c => deck?.commanders.includes(c.name));
  const companion = allScryfallCards?.find(c => c.name === deck?.companion);

  const uniqueScryfallCards = scryfallCards
    ? Array.from(new Map(scryfallCards.map(card => [`${card.id}-${(card as ScryfallCardWithCount).is_foil}`, card])).values())
    : [];

  const groupedCardList = deck && scryfallCards ? uniqueScryfallCards.map(scryfallCard => {
    const deckCard = deck.cards.find(dc =>
      dc.name === scryfallCard.name &&
      (dc.set_id ? dc.set_id.toLowerCase() === scryfallCard.set.toLowerCase() : true) &&
      (dc.art_id ? dc.art_id === scryfallCard.collector_number : true)
    );
    return {
      ...scryfallCard,
      count: deckCard?.quantity || 1,
      is_foil: deckCard?.foil,
    } as ScryfallCardWithCount;
  }).filter(c => c.id) : [];

  const uniqueSideboardCards = sideboardCards
    ? Array.from(new Map(sideboardCards.map(card => [`${card.id}-${(card as ScryfallCardWithCount).is_foil}`, card])).values())
    : [];

  const groupedSideboardList = deck && sideboardCards ? uniqueSideboardCards.map(scryfallCard => {
    const deckCard = deck.sideboard.find(dc =>
      dc.name === scryfallCard.name &&
      (dc.set_id ? dc.set_id.toLowerCase() === scryfallCard.set.toLowerCase() : true) &&
      (dc.art_id ? dc.art_id === scryfallCard.collector_number : true)
    );
    return {
      ...scryfallCard,
      count: deckCard?.quantity || 1,
      is_foil: deckCard?.foil,
    } as ScryfallCardWithCount;
  }).filter(c => c.id) : [];

  const allCards = [...groupedCardList, ...groupedSideboardList];

  const tcgPlayerMassEntryUrl = `${config.tcgPlayerAffiliateUrl}?subId1=deck-list-buy-all&u=https%3A%2F%2Fwww.tcgplayer.com%2Fmassentry%3Fproductline%3DMagic%26c%3D` +
    allCards.map(c => {
      const cardName = (c.layout === 'transform' || c.layout === 'modal_dfc' || c.layout === 'flip') ? c.name.split(' // ')[0] : c.name;
      return encodeURIComponent(`${c.count} ${cardName}`);
    }).join('%7C%7C');

  useSeoMeta({
    title: `${listTitle} - Surveil`,
    description: `View the Magic: The Gathering card list: ${listTitle}`,
  });

  const sortCards = (cards: ScryfallCardWithCount[], sortBy: string, sortOrder: string) => {
    return [...cards].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (sortBy) {
        case 'name':
          aValue = a.name;
          bValue = b.name;
          break;
        case 'cmc':
          aValue = a.cmc;
          bValue = b.cmc;
          break;
        case 'rarity': {
          const rarityOrder = ['common', 'uncommon', 'rare', 'mythic'];
          aValue = rarityOrder.indexOf(a.rarity);
          bValue = rarityOrder.indexOf(b.rarity);
          break;
        }
        case 'color': {
          const colorOrder = ['W', 'U', 'B', 'R', 'G', 'C'];
          aValue = colorOrder.indexOf(a.color_identity[0]);
          bValue = colorOrder.indexOf(b.color_identity[0]);
          break;
        }
        default:
          aValue = a.name;
          bValue = b.name;
      }

      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const filteredCards = groupedCardList.filter(card =>
    card.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupCards = (cards: ScryfallCardWithCount[], option: GroupByOption) => {
    if (option === 'none' || !cards) {
      return { 'all': sortCards(cards, sortBy, sortOrder) };
    }

    const groupedByCards = cards.reduce((acc, card) => {
      let key: string;
      if (option === 'type') {
        key = getPrimaryCardType(card);
      } else if (option === 'color') {
        const colorIdentity = card.color_identity;
        if (colorIdentity.length === 0) {
          key = 'Colorless';
        } else if (colorIdentity.length > 1) {
          key = 'Multicolored';
        } else {
          const colorMap: Record<string, string> = { 'W': 'White', 'U': 'Blue', 'B': 'Black', 'R': 'Red', 'G': 'Green' };
          key = colorMap[colorIdentity[0]];
        }
      } else {
        key = 'all';
      }

      if (!acc[key]) {
        acc[key] = [];
      }
      if (!acc[key].some(c => c.id === card.id && c.is_foil === card.is_foil)) {
        acc[key].push(card);
      }

      return acc;
    }, {} as Record<string, ScryfallCardWithCount[]>);

    // Sort cards within each group
    for (const key in groupedByCards) {
      groupedByCards[key] = sortCards(groupedByCards[key], sortBy, sortOrder);
    }

    return groupedByCards;
  };

  const displayedCards = groupCards(filteredCards, groupBy);
  const sortedGroups = Object.keys(displayedCards).sort((a, b) => {
    if (groupBy === 'type') {
      const typeOrder = ['Creature', 'Artifact', 'Enchantment', 'Planeswalker', 'Battle', 'Instant', 'Sorcery', 'Land', 'Tribal'];
      return typeOrder.indexOf(a) - typeOrder.indexOf(b);
    }
    if (groupBy === 'color') {
      const colorOrder = ['White', 'Blue', 'Black', 'Red', 'Green', 'Multicolored', 'Colorless'];
      return colorOrder.indexOf(a) - colorOrder.indexOf(b);
    }
    return 0;
  });

  const handleOpenSheet = () => {
    refetchList();
    refetchCards();
    setIsEditSheetOpen(true);
  }

  const { mutate: publish } = useNostrPublish();

  const handleQuickAdd = (card: ScryfallCard) => {
    if (!deck) return;

    const existingCardIndex = deck.event.tags.findIndex(
      (tag) => tag[0] === 'c' && tag[1] === card.name && tag[3] === card.set && tag[4] === card.collector_number
    );

    let newTags;
    if (existingCardIndex > -1) {
      newTags = [...deck.event.tags];
      const existingTag = newTags[existingCardIndex];
      const newQuantity = parseInt(existingTag[2], 10) + 1;
      newTags[existingCardIndex] = [...existingTag.slice(0, 2), newQuantity.toString(), ...existingTag.slice(3)];
    } else {
      const newCardTag = ['c', card.name, '1', card.set, card.collector_number, ''];
      newTags = [...deck.event.tags, newCardTag];
    }

    const newEvent = {
      kind: deck.event.kind,
      content: deck.event.content,
      tags: newTags,
      created_at: Math.floor(Date.now() / 1000),
    };

    publish(newEvent, {
      onSuccess: () => {
        refetchList();
        refetchCards();
      }
    });
  }



  const updateList = (newTags: string[][]) => {
    if (!deck) return;

    const newEvent = {
      kind: deck.event.kind,
      content: deck.event.content,
      tags: newTags,
      created_at: Math.floor(Date.now() / 1000),
    };

    publish(newEvent, {
      onSuccess: () => {
        refetchList();
        refetchCards();
      }
    });
  };


  const handleDeleted = () => {
    navigate('/');
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditableTitle(e.target.value);
  };

  const handleTitleSave = () => {
    if (!deck || !editableTitle.trim() || editableTitle.trim() === deck.title) {
      setIsEditing(false);
      return;
    }

    const newTags = deck.event.tags.filter(t => t[0] !== 'title');
    newTags.push(['title', editableTitle.trim()]);

    const newEvent = {
      ...deck.event,
      tags: newTags,
    };

    // Optimistically update the title in the cache
    queryClient.setQueryData(['list', naddr], {
      ...deck,
      title: editableTitle.trim(),
      event: newEvent,
    });

    updateList(newTags);
    setIsEditing(false);
  };

  const handleFetchRecommendations = useCallback(() => {
    if (!deck || !scryfallCards) return;

    const cards = scryfallCards.map((c) => `1 ${c.name}`);
    const commanders = deck.commanders.map((c) => `1 ${c} *CMDR*`);

    getRecommendations({
      cards: [...cards, ...commanders],
      commanders: deck.commanders,
    });
  }, [deck, scryfallCards, getRecommendations]);

  useEffect(() => {
    if (view === 'edhrec' && !edhrecData) {
      handleFetchRecommendations();
    }
  }, [view, edhrecData, handleFetchRecommendations]);



  const filteredCardsByStat = activeFilter
    ? groupedCardList.filter(card => {
        switch (activeFilter.type) {
            case 'cmc':
                if (activeFilter.value === 7) return card.cmc >= 7;
                return card.cmc === activeFilter.value;
            case 'legality':
                return cardIsLegal(card, listFormat) === (activeFilter.value === 'legal');
            case 'color':
                if (typeof activeFilter.value === 'string') {
                    if (activeFilter.value === 'C') return card.color_identity.length === 0;
                    return card.color_identity.includes(activeFilter.value);
                }
                return false;
            case 'type': {
                if (typeof activeFilter.value !== 'string') return false;

                const cardTypes = getCardTypes(card);

                // If the card doesn't have the type we're looking for, it's out immediately.
                if (!cardTypes.includes(activeFilter.value)) {
                    return false;
                }

                // If the filter is for a type other than "Land", but the card is a "Basic Land", exclude it.
                // This prevents basic lands from showing up in "Creature", "Artifact", etc., filters.
                if (activeFilter.value !== 'Land' && card.type_line.startsWith('Basic Land')) {
                    return false;
                }

                // If the card has the right type and isn't an excluded basic land, include it.
                return true;
            }
            default:
                return false;
        }
    })
    : [];

  const renderDeckView = () => (
    <>
      {/* Mobile/Tablet: Show stats at top (below lg breakpoint) */}
      {scryfallCards && (
        <div className="my-8 lg:hidden">
          <ListStats
    mainboardCards={groupedCardList}
    sideboardCards={groupedSideboardList}
    format={listFormat}
    commanders={commanders}
    companion={companion}
    activeFilter={activeFilter}
    setActiveFilter={setActiveFilter}
    filteredCards={filteredCardsByStat}
    onCardClick={setDetailsCard}
    cardIsLegal={cardIsLegal}
    getCardTypes={getCardTypes}
/>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-8">
        <div className="lg:col-span-2 xl:col-span-3">
          <div className="flex flex-col sm:flex-row justify-between items-end mb-4 gap-4">
            <div className="flex-grow-0 flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
              {user?.pubkey === authorPubkey && deck && (
                <div className="relative w-full sm:w-64">
                  <label className="text-xs text-muted-foreground">Quick Add</label>
                  <QuickAddBar onCardAdded={handleQuickAdd} />
                </div>
              )}
              <div className="relative w-full sm:w-64">
                <label className="text-xs text-muted-foreground">Quick Filter</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    placeholder="Filter by name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 h-8"
                  />
                </div>
              </div>
            </div>
            <div className="flex gap-4 w-full sm:w-auto">
              <div className="flex-grow">
                <label className="text-xs text-muted-foreground">Sort</label>
                <SortControls sortBy={sortBy} setSortBy={setSortBy} sortOrder={sortOrder} setSortOrder={setSortOrder} />
              </div>
              <div className="flex-grow">
                <label className="text-xs text-muted-foreground">Group by</label>
                <Select value={groupBy} onValueChange={(value: GroupByOption) => value && setGroupBy(value)}>
                  <SelectTrigger className="w-full h-8 text-xs">
                    <SelectValue placeholder="Group by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="type">Type</SelectItem>
                    <SelectItem value="color">Color</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="space-y-8">
            {isLoadingCards ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                {Array.from({ length: 15 }).map((_, i) => <Skeleton key={i} className="h-full aspect-[5/7] rounded-lg" />)}
              </div>
            ) : (
              sortedGroups.map(group => (
                <div key={group}>
                  {groupBy !== 'none' && <h3 className="text-xl font-bold mb-4">{group} ({displayedCards[group].reduce((acc, card) => acc + card.count, 0)})</h3>}
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-2 sm:gap-4">
                    {displayedCards[group].map((card) => (
                      <div key={`${card.id}-${card.is_foil}`} className="relative group">
                        <MTGCard
                          card={card}
                          count={card.count}
                          onClick={() => setDetailsCard(card)}
                          highlight={activeFilter ? filteredCardsByStat.some(c => c.id === card.id) : false}
                        />
                        {user?.pubkey === authorPubkey && (
                          <CardMenu
                            card={card}
                            listEvent={deck?.event || null}
                            onUpdate={() => {
                              refetchList();
                              refetchCards();
                            }}
                            onChangeArt={() => setArtChangeCard(card)}
                            isCommander={deck?.commanders.includes(card.name) ?? false}
                            isCompanion={deck?.companion === card.name}
                            onSetCommander={(card) => {
                              if (!deck) return;
                              if (deck.commanders.length >= 2) return;
                              const newTags = [...deck.event.tags];
                              newTags.push(['C', card.name]);
                              updateList(newTags);
                            }}
                            onUnsetCommander={(card) => {
                              if (!deck) return;
                              const newTags = deck.event.tags.filter(t => t[0] !== 'C' || t[1] !== card.name);
                              updateList(newTags);
                            }}
                            onSetCompanion={(card) => {
                              if (!deck) return;
                              const newTags = deck.event.tags.filter(t => t[0] !== 'S');
                              newTags.push(['S', card.name]);
                              updateList(newTags);
                            }}
                            onUnsetCompanion={() => {
                              if (!deck) return;
                              const newTags = deck.event.tags.filter(t => t[0] !== 'S');
                              updateList(newTags);
                            }}
                            onSetBanner={(card) => {
                              if (!deck) return;
                              const newTags = deck.event.tags.filter(t => t[0] !== 'banner');
                              newTags.push(['banner', card.image_uris?.art_crop || card.image_uris?.large || card.image_uris?.normal || '']);
                              updateList(newTags);
                            }}
                            className="absolute top-[12%] right-[9%] sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
          {groupedSideboardList.length > 0 && (
            <div className="mt-8">
              <h3 className="text-2xl font-bold mb-4">Sideboard ({groupedSideboardList.reduce((acc, card) => acc + card.count, 0)})</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4">
                {groupedSideboardList.map((card) => (
                  <div key={`${card.id}-${card.is_foil}`} className="relative group">
                    <MTGCard
                      card={card}
                      count={card.count}
                      onClick={() => setDetailsCard(card)}
                    />
                    {user?.pubkey === authorPubkey && (
                      <CardMenu
                        card={card}
                        listEvent={deck?.event || null}
                        onUpdate={() => {
                          refetchList();
                          refetchCards();
                          refetchSideboard();
                        }}
                        onChangeArt={() => setArtChangeCard(card)}
                        isCommander={false}
                        isCompanion={false}
                        isSideboard
                        onSetCommander={() => {}}
                        onSetCompanion={() => {}}
                        onSetBanner={() => {}}
                        onUnsetCommander={() => {}}
                        onUnsetCompanion={() => {}}
                        className="absolute top-[12%] right-[9%] sm:opacity-0 sm:group-hover:opacity-100 sm:transition-opacity"
                      />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        {/* Desktop: Show stats in sidebar (lg and above) */}
        <aside className="hidden lg:block lg:col-span-1">
          <div>
            {scryfallCards && <ListStats
    mainboardCards={groupedCardList}
    sideboardCards={groupedSideboardList}
    format={listFormat}
    commanders={commanders}
    companion={companion}
    activeFilter={activeFilter}
    setActiveFilter={setActiveFilter}
    filteredCards={filteredCardsByStat}
    onCardClick={setDetailsCard}
    cardIsLegal={cardIsLegal}
    getCardTypes={getCardTypes}
/>}
          </div>
        </aside>
      </div>
    </>
  );

  const renderEDHRecView = () => (
    <div className="space-y-4">
      <Button onClick={() => setView('deck')} variant="ghost">
        <ArrowLeft className="mr-2 h-4 w-4" /> Back to Deck
      </Button>

      {isEdhrecError && (
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to fetch EDHREC recommendations. Please try again later.
          </AlertDescription>
        </Alert>
      )}

      {edhrecData && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {edhrecData.map((card) => (
            <EDHRecCard
              key={card.oracle_id}
              card={card}
              onCardAdded={handleQuickAdd}
              onCardClick={setDetailsCard}
            />
          ))}
        </div>
      )}

      {isEdhrecPending && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {Array.from({ length: 10 }).map((_, i) => (
            <Skeleton key={i} className="w-full h-64" />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      <main className="pb-8 relative">
      <div className="relative h-64 overflow-hidden mb-8 z-0">
        {imageUrl && (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center filter blur-lg scale-105"
              style={{ backgroundImage: `url(${imageUrl})` }}
            />
            <div className="absolute inset-0 bg-black/50" />
          </>
        )}
        <div className="relative h-full">
          <div
            className="container h-full bg-cover bg-center mx-auto"
            style={{ backgroundImage: `url(${imageUrl})` }}
          >
            <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-8">
              <div className="container mx-auto px-0 sm:px-4 sm:px-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4">
                  <div className="flex-grow">
                    <div className="flex items-center gap-2">
                      {isEditing ? (
                        <Input
                          value={editableTitle}
                          onChange={handleTitleChange}
                          onBlur={handleTitleSave}
                          onKeyDown={(e) => e.key === 'Enter' && handleTitleSave()}
                          className="text-2xl sm:text-4xl font-bold text-white bg-transparent border-b-2 border-white/50 focus:border-white"
                          autoFocus
                        />
                      ) : (
                        <h2
                          className="text-2xl sm:text-4xl font-bold text-white shadow-2xl cursor-pointer"
                          onClick={() => user?.pubkey === authorPubkey && setIsEditing(true)}
                        >
                          {listTitle}
                        </h2>
                      )}
                      {isUnlisted && (
                        <div className="bg-background/80 rounded-full p-1.5">
                          <EyeOff className="h-5 w-5 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-2">
                      {authorPubkey && <Author pubkey={authorPubkey} />}
                      {listFormats.length > 0 && (
                        <div className="flex gap-2">
                          {listFormats.map(format => (
                            <Badge key={format} variant="secondary" className="text-sm sm:text-lg shadow-2xl">{kebabToTitleCase(format)}</Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-auto">
                    {groupedCardList.length > 0 && (
                      <>
                        <Button asChild variant="secondary" size="sm">
                          <a href={tcgPlayerMassEntryUrl} target="_blank" rel="noopener noreferrer">
                            <ShoppingBag className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Buy Deck</span>
                          </a>
                        </Button>
                        <PrintDecklistButton
                          cards={allCards}
                          title={listTitle}
                        />
                      </>
                    )}
                    {deck && (
                      <ListActions
                        listEvent={deck.event}
                        onShare={() => setIsShareDialogOpen(true)}
                        onEdit={handleOpenSheet}
                        onDeleted={handleDeleted}
                        isOwner={user?.pubkey === authorPubkey}
                        onEDHRec={() => setView('edhrec')}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="container mx-auto px-4 sm:px-6 relative z-0">
        {((!deck && (!!naddr || !!state?.deck)) || (deck && (isLoadingCards || _isLoadingSideboard))) && (
          <div className="fixed inset-0 bg-background/60 backdrop-blur-md z-50 flex items-center justify-center">
            <ListLoadingIndicator
              isLoadingNostr={!deck && (!!naddr || !!state?.deck) ? true : false}
              isLoadingScryfall={deck && (isLoadingCards || _isLoadingSideboard) ? true : false}
              variant="overlay"
              title="Take your time."
            />
          </div>
        )}

        <div className="relative">
          {view === 'deck' ? renderDeckView() : renderEDHRecView()}
        </div>
      </div>
      {deck && (
        <EditListSheet
          open={isEditSheetOpen}
          onOpenChange={setIsEditSheetOpen}
          deck={deck}
          cards={groupedCardList}
          sideboardCards={groupedSideboardList}
        />
      )}
      {deck && (
        <ShareListDialog
          open={isShareDialogOpen}
          onOpenChange={setIsShareDialogOpen}
          deck={deck}
          deckUrl={window.location.href}
        />
      )}
      {detailsCard && (
        <CardDetailsDialog
          card={detailsCard}
          open={!!detailsCard}
          onOpenChange={(open) => !open && setDetailsCard(null)}
        />
      )}
      {artChangeCard && (
        <ChangeArtDialog
          card={artChangeCard}
          cards={groupedCardList}
          listEvent={deck?.event || null}
          open={!!artChangeCard}
          onOpenChange={(open) => {
            if (!open) {
              setArtChangeCard(null);
            }
            refetchList();
            refetchCards();
          }}
        />
      )}
    </main>
    </>
  );
};

export default ListPage;
